import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiSearch } from 'react-icons/fi';
import Header from '../../components/header/Header.tsx';
import { useUserContext } from '../../user/context/UserContext.tsx';
import { HOST } from '../../utils/constants.ts';
import {
  formatPriceWithDots,
  isLogged,
  removePriceFormat,
} from '../../utils/utils.ts';
import { useProductContext } from '../context/ProductContext.tsx';
import {
  Product,
  ProductOptionValues,
  ProductType,
  productTypeOptions,
} from '../ProductType.ts';
import { getWeightLabel } from '../utils.ts';
import './price-list.scss';
import PriceProductSection from './PriceProductSection.tsx';

interface ProductAuditLastUpdate {
  lastUpdate: string;
  updatedBy: string;
  daysAgo: number;
  isOutdated: boolean;
}

const PriceList = () => {
  const { products, setProducts } = useProductContext();
  const [editedProducts, setEditedProducts] = useState(products);
  const [editedFields, setEditedFields] = useState<Record<string, Set<string>>>(
    {}
  );
  const [searchProduct, setSearchProduct] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('');
  const [lastUpdateAudit, setLastUpdateAudit] =
    useState<ProductAuditLastUpdate | null>(null);
  const [isLoadingLastUpdate, setIsLoadingLastUpdate] = useState(false);
  const { userInfo } = useUserContext();
  const isAdmin = userInfo?.user?.isAdmin;

  const chipsRef = useRef<HTMLDivElement | null>(null);
  const [chipsAtEnd, setChipsAtEnd] = useState(false);

  useEffect(() => {
    if (!chipsRef.current) return;
    const activeChip = chipsRef.current.querySelector<HTMLButtonElement>(
      '.category-chip.active'
    );
    activeChip?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [productTypeFilter]);

  useEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    const check = () =>
      setChipsAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
    check();
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, []);

  // Opciones de chips para filtrar por tipo
  const typeFilterOptions = [
    { value: '', label: 'Todos' },
    ...productTypeOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  ];

  useEffect(() => {
    setEditedProducts(products);
  }, [products]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${HOST}/products`, {
          headers: {
            Authorization: `Bearer ${isLogged()}`,
          },
        });

        if (!response.ok) {
          throw new Error('Error al cargar productos');
        }

        const data: Product[] = await response.json();

        // Formatear precios con puntos para mostrar en el input
        const formattedData = data.map((product) => ({
          ...product,
          price: formatPriceWithDots(product.price),
        }));

        setProducts(formattedData);
        setEditedProducts(formattedData);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchProducts();
  }, [setProducts]);

  const fetchLastUpdateAudit = async () => {
    if (!isAdmin) return;

    try {
      setIsLoadingLastUpdate(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${HOST}/products/audit/last-update`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar auditoría de precios');
      }

      const data: ProductAuditLastUpdate = await response.json();
      setLastUpdateAudit(data);
    } catch (error) {
      console.error('Error fetching last update audit:', error);
    } finally {
      setIsLoadingLastUpdate(false);
    }
  };

  useEffect(() => {
    fetchLastUpdateAudit();
  }, [isAdmin]);

  const formatAuditDateTime = (isoDate?: string) => {
    if (!isoDate) return 'N/D';

    const parsedDate = new Date(isoDate);
    if (Number.isNaN(parsedDate.getTime())) return 'N/D';

    const date = parsedDate.toLocaleDateString('es-AR');
    const time = parsedDate.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return `${date} ${time}`;
  };

  const normalize = (str: string) =>
    str
      .toLowerCase()
      .normalize('NFD') // divide letras con tilde
      .replace(/[\u0300-\u036f]/g, '') // remueve tildes
      .replace(/\s+/g, ' ') // reemplaza espacios múltiples
      .trim();

  const filteredProducts = editedProducts.filter((p) => {
    const matchesSearch = normalize(p.name).includes(normalize(searchProduct));
    const matchesType =
      !productTypeFilter || p.productType === productTypeFilter;
    return matchesSearch && matchesType;
  });

  const verduras = filteredProducts.filter(
    (p) => p.productType === ProductType.VEGETABLE
  );
  const frutas = filteredProducts.filter(
    (p) => p.productType === ProductType.FRUIT
  );
  const condimentos = filteredProducts.filter(
    (p) => p.productType === ProductType.CONDIMENTS
  );
  const secos = filteredProducts.filter(
    (p) => p.productType === ProductType.DRIED
  );
  const otros = filteredProducts.filter(
    (p) => p.productType === ProductType.OTHERS
  );

  // const verduras = filteredProducts.filter((p) => p.productType === 'verdura');
  // const frutas = filteredProducts.filter((p) => p.productType === 'fruta');
  // const condimentos = filteredProducts.filter(
  //   (p) => p.productType === 'condimentos',
  // );
  // const otros = filteredProducts.filter(
  //   (p) => p.productType === 'otros' || p.productType === 'otro',
  // );

  const markFieldAsEdited = (id: string, field: string) => {
    setEditedFields((prev) => ({
      ...prev,
      [id]: new Set([...(prev[id] || []), field]),
    }));
  };

  const handlePriceChange = (id: string, newPrice: string) => {
    const formattedPrice = formatPriceWithDots(newPrice);
    markFieldAsEdited(id, 'price');
    setEditedProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, price: formattedPrice } : p))
    );
  };

  const handleNameChange = (id: string, newName: string) => {
    markFieldAsEdited(id, 'name');
    setEditedProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, name: newName } : p))
    );
  };

  const handleBoxWeightChange = (id: string, newBoxWeight: string) => {
    markFieldAsEdited(id, 'boxWeight');
    setEditedProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, boxWeight: newBoxWeight } : p))
    );
  };

  const handleStockChange = (id: string, hasStock: boolean) => {
    markFieldAsEdited(id, 'hasStock');
    setEditedProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, hasStock } : p))
    );
  };

  const buildWeightLabel = (product: Product): string => {
    if (!product?.boxWeight || !product?.purchaseOptions) return '';

    // Obtener la primera opción de compra
    let purchaseOption: ProductOptionValues;

    if (typeof product.purchaseOptions === 'string') {
      purchaseOption = product.purchaseOptions
        .split(',')[0]
        .trim() as ProductOptionValues;
    } else if (Array.isArray(product.purchaseOptions)) {
      purchaseOption = product.purchaseOptions[0] as ProductOptionValues;
    } else {
      return '';
    }

    // Usar getWeightLabel con cantidad 1 para la lista de precios
    const weightLabel = getWeightLabel(
      product.boxWeight,
      1,
      purchaseOption,
      product.productType
    );

    // Remover "peso: " del resultado para la lista de precios
    return weightLabel.replace(/^peso:\s*/i, '');
  };

  const handleSave = async () => {
    const hasInvalidPrices = editedProducts.some(
      (p) => !p.price || isNaN(Number(p.price))
    );

    if (hasInvalidPrices) {
      toast.error('Todos los productos deben tener un precio válido');
      return;
    }

    // Filtrar solo los productos que realmente fueron editados
    const productsWithChanges = editedProducts.filter(
      (product) => editedFields[product.id] && editedFields[product.id].size > 0
    );

    if (productsWithChanges.length === 0) {
      toast.error('No hay cambios para guardar');
      return;
    }

    console.log(
      `Enviando ${productsWithChanges.length} productos modificados de ${editedProducts.length} totales`
    );

    try {
      await updateProductPrices(productsWithChanges);
    } catch (error) {
      toast.error('Error al guardar los cambios');
    }
    setEditedFields({});
  };

  const updateProductPrices = async (updatedProducts: Product[]) => {
    try {
      const token = localStorage.getItem('token');

      // Crear payload optimizado con solo las propiedades esenciales
      const optimizedPayload = updatedProducts.map((product) => ({
        id: product.id,
        name: product.name,
        price: removePriceFormat(product.price), // Remover puntos antes de enviar
        boxWeight: product.boxWeight,
        hasStock: product.hasStock ?? true,
        // Incluir solo si fueron modificados otros campos relevantes
        ...(editedFields[product.id]?.has('productType') && {
          productType: product.productType,
        }),
        ...(editedFields[product.id]?.has('purchaseOptions') && {
          purchaseOptions: product.purchaseOptions,
        }),
      }));

      const response = await fetch(`${HOST}/products/update-prices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(optimizedPayload),
      });

      if (!response.ok) {
        throw new Error('Error al actualizar los precios');
      }

      const updatedProductsFromServer = await response.json();
      toast.success(
        `${optimizedPayload.length} precios actualizados con éxito`
      );

      // Actualizar los productos en el estado con los datos del servidor
      setEditedProducts((prev) =>
        prev.map((product) => {
          const updated = updatedProductsFromServer.find(
            (up: Product) => up.id === product.id
          );
          return updated ? updated : product;
        })
      );

      setProducts(editedProducts);
      await fetchLastUpdateAudit();
    } catch (error) {
      console.error('Error al actualizar los precios:', error);
      throw error;
    }
  };

  const exportToPDF = async () => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const date = new Date();
      const formattedDate = `${String(date.getDate()).padStart(
        2,
        '0'
      )}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const loadImage = (src: string): Promise<string> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = reject;
          img.src = src;
        });
      };

      const logoData = await loadImage('/rapi-fruti-logo.jpeg');
      doc.addImage(logoData, 'JPEG', 10, 10, 30, 30);

      // 👉 Imagen de fondo con transparencia
      const backgroundData = await loadImage(
        `${window.location.origin}/logo-transparencia.png`
      );

      doc.addImage(
        backgroundData,
        'PNG',
        pageWidth / 4,
        pageHeight / 4,
        pageWidth / 2,
        pageWidth / 2,
        '',
        'FAST'
      );

      doc.setFontSize(22);
      doc.setTextColor(34, 139, 34);
      doc.text('Listado de Precios', 45, 20);

      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Fecha: ${formattedDate}`, 45, 28);

      const startY = 40;
      let currentY = startY;

      const addTable = (title: string, data: Product[]) => {
        if (data.length === 0) return;

        doc.setTextColor(255);
        doc.setFillColor(46, 139, 87);
        doc.rect(10, currentY, pageWidth - 20, 8, 'F');
        doc.text(title, pageWidth / 2, currentY + 6, { align: 'center' });
        currentY += 10;

        const rows = data.map((p) => [
          p.name,
          buildWeightLabel(p),
          `$${p.price}`,
        ]);

        autoTable(doc, {
          startY: currentY,
          head: [['Producto', 'Cant.', 'Precio']],
          body: rows,
          styles: { fontSize: 10, halign: 'center' },
          headStyles: {
            fillColor: [85, 180, 100],
            textColor: 255,
            fontStyle: 'bold',
          },
          columnStyles: {
            0: { cellWidth: 70 },
            1: { cellWidth: 40 },
            2: { cellWidth: 40 },
          },
          margin: { left: (pageWidth - 150) / 2 },
          tableWidth: 'wrap',
          didDrawPage: (data) => {
            if (data.cursor) currentY = data.cursor.y + 10;
          },
        });
      };

      addTable('VERDURAS', verduras);
      addTable('FRUTAS', frutas);
      addTable('CONDIMENTOS', condimentos);
      addTable('SECOS', secos);
      addTable('OTROS', otros);

      doc.save(`listado-precios-rapifruti-${formattedDate}.pdf`);
      toast.success('PDF descargado correctamente.');
    } catch (error) {
      console.error('Error exportando a PDF', error);
      toast.error('Hubo un error al generar la lista de precios.');
    }
  };

  return (
    <div>
      <Header />
      <div className="price-list-container">
        <h2 className="margin-0">Listado de Precios</h2>
        {isAdmin && (
          <div className="price-list-audit" role="status" aria-live="polite">
            {isLoadingLastUpdate ? (
              <span>Cargando última actualización...</span>
            ) : (
              <span>
                Última actualización:{' '}
                {formatAuditDateTime(lastUpdateAudit?.lastUpdate)} por{' '}
                {lastUpdateAudit?.updatedBy || 'N/D'}
              </span>
            )}
          </div>
        )}
        <div className="search-filter-row">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              className="search-input"
              placeholder="Busca tu producto..."
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
            />
          </div>
          <div className={`chips-scroll-wrapper${chipsAtEnd ? ' at-end' : ''}`}>
            <div className="category-chips" ref={chipsRef}>
              {typeFilterOptions.map((option) => (
                <button
                  key={option.value}
                  className={`category-chip ${productTypeFilter === option.value ? 'active' : ''}`}
                  onClick={() => setProductTypeFilter(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '10px',
          }}
        >
          <button
            type="button"
            className="primary-button"
            onClick={exportToPDF}
          >
            Exportar lista de precios
          </button>
        </div>

        <div className="table-container">
          <table className="price-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Cant.</th>
                <th>Precio ($)</th>
                {isAdmin && <th>Stock</th>}
              </tr>
            </thead>
            <tbody>
              {[
                { title: 'Verduras', products: verduras },
                { title: 'Frutas', products: frutas },
                { title: 'Condimentos', products: condimentos },
                { title: 'Secos', products: secos },
                { title: 'Otros', products: otros },
              ].map(({ title, products }) => (
                <PriceProductSection
                  key={title}
                  title={title}
                  products={products}
                  isAdmin={isAdmin}
                  editedFields={editedFields}
                  handleNameChange={handleNameChange}
                  handlePriceChange={handlePriceChange}
                  handleBoxWeightChange={handleBoxWeightChange}
                  handleStockChange={handleStockChange}
                  buildWeightLabel={buildWeightLabel}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="bottom-buttons margin-bottom-20">
          {isAdmin && (
            <button
              type="button"
              className="primary-button"
              onClick={handleSave}
              style={{ marginTop: '10px' }}
            >
              Guardar cambios
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceList;
